import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDN } from '../../contexts/DNContext';
import { API_BASE_URL } from '../../config';
import Select from 'react-select';
import { useSelectStyles } from '../../utils/selectStyles';
import LoadingSpinner from '../LoadingSpinner';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MAPBOX_ACCESS_TOKEN } from '../../config';
import { useLocation } from 'react-router-dom';
import { _t, _n } from '../../utils/i18n';

// Configuration Mapbox
mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

// Composant Switch personnalisé
const ToggleSwitch = ({ label, checked, onChange }) => {
  return (
    <div className="flex items-center gap-2">
      <label className="inline-flex items-center cursor-pointer">
        <input
          type="checkbox"
          className="sr-only peer"
          checked={checked}
          onChange={onChange}
        />
        <div className="relative w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
        <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
      </label>
    </div>
  );
};

const MapVisualizationSelector = ({ activeViz, onChange }) => {
  const options = [
    { value: 'issues', label: _t('Distribution problems') },
    { value: 'product', label: _t('Product presence') }
  ];

  return (
    <div className="flex items-center bg-white dark:bg-gray-700 p-2 rounded-lg shadow mb-4">
      <div className="mr-4 text-sm font-medium text-gray-700 dark:text-gray-300">
        {_t('Visualization')}:
      </div>
      <div className="flex space-x-2">
        {options.map(option => (
          <button
            key={option.value}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              activeViz === option.value
                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
            }`}
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
};

// Modifiez le composant MapInstance pour gérer correctement les événements et les sources
const MapInstance = ({ type, data, isVisible, showOutOfRange = true }) => {
  const mapContainer = useRef(null);
  const mapInstance = useRef(null);
  const [mapInitialized, setMapInitialized] = useState(false);
  const [sourcesAdded, setSourcesAdded] = useState(false);

  // Fonction pour nettoyer toutes les couches et sources
  const cleanupLayers = (map) => {
    try {
      // Nettoyer les événements en une seule fois - plus simple et plus sûr
      if (mapInstance.current) {
        ['click', 'mouseenter', 'mouseleave'].forEach(eventType => {
          map.off(eventType);
        });
      }

      // Liste de toutes les couches et sources possibles
      const layers = [
        'issues-layer', 'clusters', 'cluster-count',
        'present-stores-layer', 'absent-stores-layer', 'not-in-range-stores-layer'
      ];

      const sources = [
        'issues', 'present-stores', 'absent-stores', 'not-in-range-stores'
      ];

      // Supprimer les couches
      layers.forEach(layer => {
        if (map.getLayer(layer)) {
          map.removeLayer(layer);
        }
      });

      // Supprimer les sources
      sources.forEach(source => {
        if (map.getSource(source)) {
          map.removeSource(source);
        }
      });

      setSourcesAdded(false);
    } catch (error) {
      console.error("Erreur lors du nettoyage des couches et sources :", error);
    }
  };

  // Fonction pour initialiser la carte
  const setupMap = () => {
    if (!mapContainer.current) return;

    console.log(`Création d'une nouvelle carte pour ${type}`);

    // Créer la carte
    const map = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/light-v10',
      center: [2.213749, 46.227638], // Centre de la France
      zoom: 5
    });

    // Ajouter les contrôles
    map.addControl(new mapboxgl.NavigationControl({ visualizePitch: false }));

    // Attendre que la carte soit chargée
    map.on('load', () => {
      console.log(`Carte ${type} chargée avec succès`);
      mapInstance.current = map;
      setMapInitialized(true);
    });

    return () => {
      if (map) {
        console.log(`Suppression de la carte ${type}`);
        map.remove();
        mapInstance.current = null;
        setMapInitialized(false);
        setSourcesAdded(false);
      }
    };
  };

  // Initialiser la carte quand elle devient visible
  useEffect(() => {
    if (!isVisible) return;

    // Nettoyer une carte existante si nécessaire
    if (mapInstance.current) {
      mapInstance.current.remove();
      mapInstance.current = null;
      setMapInitialized(false);
      setSourcesAdded(false);
    }

    const cleanup = setupMap();

    return cleanup;
  }, [isVisible, type]);

  // Mise à jour des données sur la carte
  useEffect(() => {
    if (!mapInitialized || !mapInstance.current || !data || !isVisible) return;

    const map = mapInstance.current;

    // Cas spécial pour showOutOfRange: si seul ce paramètre a changé et que la couche existe déjà
    if (type === 'product' && sourcesAdded && map.getLayer('not-in-range-stores-layer')) {
      try {
        // Mettre à jour uniquement la visibilité de la couche
        map.setLayoutProperty(
          'not-in-range-stores-layer',
          'visibility',
          showOutOfRange ? 'visible' : 'none'
        );

        // Ajuster les limites de la carte en fonction des magasins visibles
        if (data && data.stores) {
          const validStores = data.stores.filter(store =>
            store.latitude && store.longitude &&
            !isNaN(parseFloat(store.latitude)) &&
            !isNaN(parseFloat(store.longitude))
          );

          const presentStores = validStores.filter(store => store.isPresent);
          const absentStores = validStores.filter(store => !store.isPresent && store.reasonCategory !== 'Not in Store Range');

          const storesForBounds = showOutOfRange
            ? validStores
            : [...presentStores, ...absentStores];

          if (storesForBounds.length > 0) {
            const bounds = new mapboxgl.LngLatBounds();
            storesForBounds.forEach(store => {
              bounds.extend([parseFloat(store.longitude), parseFloat(store.latitude)]);
            });

            map.fitBounds(bounds, {
              padding: 50,
              maxZoom: 15
            });
          }
        }

        // Ne pas continuer si on a juste mis à jour la visibilité
        return;
      } catch (err) {
        console.error("Erreur lors de la mise à jour de la visibilité de la couche:", err);
        // Continuer avec la logique normale si une erreur se produit
      }
    }

    // Attendre que la carte soit complètement chargée
    if (!map.loaded()) {
      console.log("La carte n'est pas encore totalement chargée, attente...");

      const onLoadHandler = () => {
        updateMapData(map, data);
      };

      map.once('load', onLoadHandler);
      return () => {
        map.off('load', onLoadHandler);
      };
    }

    updateMapData(map, data);
  }, [mapInitialized, data, isVisible, type, showOutOfRange, sourcesAdded]);

  // Fonction pour mettre à jour les données sur la carte
  const updateMapData = (map, data) => {
    // Nettoyer d'abord toutes les couches et sources
    cleanupLayers(map);

    // Différentes logiques basées sur le type de carte
    if (type === 'issues') {
      updateIssuesLayer(map, data);
    } else if (type === 'product' && data && data.stores) {
      updateProductLayer(map, data, showOutOfRange);
    }

    // Forcer un resize
    setTimeout(() => {
      if (mapInstance.current) {
        mapInstance.current.resize();
      }
    }, 100);
  };

  // Fonction pour mettre à jour la couche des issues
  const updateIssuesLayer = (map, data) => {
    const validIssues = data.filter(issue =>
      issue.latitude && issue.longitude &&
      !isNaN(parseFloat(issue.latitude)) &&
      !isNaN(parseFloat(issue.longitude))
    );

    if (validIssues.length === 0) return;

    console.log(`Ajout de la source issues avec ${validIssues.length} points`);

    // Ajouter la source
    map.addSource('issues', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: validIssues.map(issue => ({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [parseFloat(issue.longitude), parseFloat(issue.latitude)]
          },
          properties: {
            title: issue.productName,
            description: issue.interventionPoint,
            shopSign: issue.shopSign,
            reason: issue.reason || 'Non spécifié'
          }
        }))
      },
      cluster: true,
      clusterMaxZoom: 14,
      clusterRadius: 50
    });

    // Ajouter les couches
    map.addLayer({
      id: 'clusters',
      type: 'circle',
      source: 'issues',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': [
          'step',
          ['get', 'point_count'],
          '#F59E0B', // amber-500
          10,
          '#EF4444', // red-500
          30,
          '#DC2626' // red-600
        ],
        'circle-radius': [
          'step',
          ['get', 'point_count'],
          20,
          10,
          25,
          30,
          30
        ]
      }
    });

    map.addLayer({
      id: 'cluster-count',
      type: 'symbol',
      source: 'issues',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 12
      },
      paint: {
        'text-color': '#FFFFFF'
      }
    });

    map.addLayer({
      id: 'issues-layer',
      type: 'circle',
      source: 'issues',
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-radius': 8,
        'circle-color': '#EF4444',
        'circle-stroke-width': 1,
        'circle-stroke-color': '#FFFFFF'
      }
    });

    // Stocker les IDs d'événements pour pouvoir les supprimer plus tard
    const eventIds = [];

    // Ajouter les popups et événements avec des identifiants uniques
    const issueClickId = `issues-layer-click-${Date.now()}`;
    eventIds.push({ type: 'click', layer: 'issues-layer', id: issueClickId });
    map.on('click', 'issues-layer', (e) => {
      const coords = e.features[0].geometry.coordinates.slice();
      const props = e.features[0].properties;

      new mapboxgl.Popup()
        .setLngLat(coords)
        .setHTML(`
          <div class="p-2">
            <div class="font-semibold">${props.title}</div>
            <div class="text-sm">${props.description}</div>
            <div class="text-sm">${props.shopSign}</div>
            <div class="text-sm mt-1"><span class="font-medium">Raison:</span> ${props.reason}</div>
          </div>
        `)
        .addTo(map);
    });

    const clusterClickId = `clusters-click-${Date.now()}`;
    eventIds.push({ type: 'click', layer: 'clusters', id: clusterClickId });
    map.on('click', 'clusters', (e) => {
      const features = map.queryRenderedFeatures(e.point, { layers: ['clusters'] });
      const clusterId = features[0].properties.cluster_id;

      map.getSource('issues').getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) return;

        map.easeTo({
          center: features[0].geometry.coordinates,
          zoom: zoom
        });
      });
    });

    // Curseur
    const issuesEnterID = `issues-mouseenter-${Date.now()}`;
    eventIds.push({ type: 'mouseenter', layer: 'issues-layer', id: issuesEnterID });
    map.on('mouseenter', 'issues-layer', () => {
      map.getCanvas().style.cursor = 'pointer';
    });

    const issuesLeaveID = `issues-mouseleave-${Date.now()}`;
    eventIds.push({ type: 'mouseleave', layer: 'issues-layer', id: issuesLeaveID });
    map.on('mouseleave', 'issues-layer', () => {
      map.getCanvas().style.cursor = '';
    });

    const clustersEnterID = `clusters-mouseenter-${Date.now()}`;
    eventIds.push({ type: 'mouseenter', layer: 'clusters', id: clustersEnterID });
    map.on('mouseenter', 'clusters', () => {
      map.getCanvas().style.cursor = 'pointer';
    });

    const clustersLeaveID = `clusters-mouseleave-${Date.now()}`;
    eventIds.push({ type: 'mouseleave', layer: 'clusters', id: clustersLeaveID });
    map.on('mouseleave', 'clusters', () => {
      map.getCanvas().style.cursor = '';
    });

    // Adapter la vue
    const bounds = new mapboxgl.LngLatBounds();

    validIssues.forEach(issue => {
      bounds.extend([parseFloat(issue.longitude), parseFloat(issue.latitude)]);
    });

    map.fitBounds(bounds, {
      padding: 50,
      maxZoom: 15
    });

    // Mettre à jour les IDs d'événements
    mapContainer.current._eventIds = eventIds;

    setSourcesAdded(true);
  };

  // Fonction pour mettre à jour la couche des produits
  const updateProductLayer = (map, data, showOutOfRange) => {
    if (!map || !data || !data.stores) {
      console.error("Données ou carte manquantes");
      return;
    }

    // Vérifier si la carte est chargée
    if (!map.loaded()) {
      console.log("La carte n'est pas encore chargée, planification de mise à jour...");
      // Définir un délai pour réessayer
      setTimeout(() => {
        if (mapInstance.current) {
          updateProductLayer(mapInstance.current, data, showOutOfRange);
        }
      }, 200);
      return;
    }

    const validStores = data.stores.filter(store =>
      store.latitude && store.longitude &&
      !isNaN(parseFloat(store.latitude)) &&
      !isNaN(parseFloat(store.longitude))
    );

    if (validStores.length === 0) {
      console.warn("Aucun magasin valide trouvé");
      return;
    }

    console.log(`Traitement de ${validStores.length} magasins (showOutOfRange=${showOutOfRange})`);

    // Obtenir les magasins par statut
    const presentStores = validStores.filter(store => store.isPresent);
    const absentStores = validStores.filter(store => !store.isPresent && store.reasonCategory !== 'Not in Store Range');
    const notInRangeStores = validStores.filter(store => !store.isPresent && store.reasonCategory === 'Not in Store Range');

    console.log(`Répartition: ${presentStores.length} présents, ${absentStores.length} absents, ${notInRangeStores.length} hors référencement`);

    // Vérifier si les sources existent déjà
    const sourcesExist = ['present-stores', 'absent-stores', 'not-in-range-stores'].some(id => {
      try {
        return map.getSource(id);
      } catch (err) {
        return false;
      }
    });

    // Si les sources existent déjà, on les met à jour au lieu de les recréer
    if (sourcesExist) {
      try {
        // Mettre à jour les sources existantes
        if (map.getSource('present-stores')) {
          map.getSource('present-stores').setData({
            type: 'FeatureCollection',
            features: presentStores.map(store => ({
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: [parseFloat(store.longitude), parseFloat(store.latitude)]
              },
              properties: {
                title: store.interventionPoint,
                shopSign: store.shopSign,
                isPresent: store.isPresent,
                reason: store.reason || 'Non spécifié',
                color: '#10B981'
              }
            }))
          });
        }

        if (map.getSource('absent-stores')) {
          map.getSource('absent-stores').setData({
            type: 'FeatureCollection',
            features: absentStores.map(store => ({
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: [parseFloat(store.longitude), parseFloat(store.latitude)]
              },
              properties: {
                title: store.interventionPoint,
                shopSign: store.shopSign,
                isPresent: store.isPresent,
                reason: store.reason || 'Non spécifié',
                color: '#EF4444'
              }
            }))
          });
        }

        if (map.getSource('not-in-range-stores')) {
          map.getSource('not-in-range-stores').setData({
            type: 'FeatureCollection',
            features: notInRangeStores.map(store => ({
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: [parseFloat(store.longitude), parseFloat(store.latitude)]
              },
              properties: {
                title: store.interventionPoint,
                shopSign: store.shopSign,
                isPresent: store.isPresent,
                reason: store.reason || 'Non spécifié',
                color: '#9CA3AF'
              }
            }))
          });
        }

        // Gérer la visibilité de la couche "hors référencement"
        if (map.getLayer('not-in-range-stores-layer')) {
          map.setLayoutProperty(
            'not-in-range-stores-layer',
            'visibility',
            showOutOfRange ? 'visible' : 'none'
          );
        }

      } catch (err) {
        console.error("Erreur lors de la mise à jour des sources:", err);
        // Si la mise à jour échoue, on nettoie tout et on recrée
        cleanupLayers(map);
        // Créer de nouvelles sources et couches
        createNewLayers();
      }
    } else {
      // Créer de nouvelles sources et couches
      createNewLayers();
    }

    // Fonction pour créer de nouvelles couches
    function createNewLayers() {
      // Fonction pour ajouter une source et une couche
      const addStoreLayer = (id, stores, color) => {
        if (stores.length === 0) return false;

        console.log(`Ajout de la source ${id} avec ${stores.length} points`);

        try {
          // Ajouter la source
          map.addSource(id, {
            type: 'geojson',
            data: {
              type: 'FeatureCollection',
              features: stores.map(store => ({
                type: 'Feature',
                geometry: {
                  type: 'Point',
                  coordinates: [parseFloat(store.longitude), parseFloat(store.latitude)]
                },
                properties: {
                  title: store.interventionPoint,
                  shopSign: store.shopSign,
                  isPresent: store.isPresent,
                  reason: store.reason || 'Non spécifié',
                  color: color
                }
              }))
            }
          });

          // Ajouter la couche avec la visibilité appropriée
          map.addLayer({
            id: `${id}-layer`,
            type: 'circle',
            source: id,
            layout: {
              visibility: id === 'not-in-range-stores' && !showOutOfRange ? 'none' : 'visible'
            },
            paint: {
              'circle-radius': 8,
              'circle-color': color,
              'circle-stroke-width': 1,
              'circle-stroke-color': '#FFFFFF'
            }
          });

          // Stocker les IDs d'événements pour pouvoir les supprimer plus tard
          const eventIds = mapContainer.current._eventIds || [];

          // Ajouter les popups avec un ID unique
          const clickHandlerId = `click-${id}-${Date.now()}`;
          eventIds.push({ type: 'click', layer: `${id}-layer`, id: clickHandlerId });
          map.on('click', `${id}-layer`, (e) => {
            const coords = e.features[0].geometry.coordinates.slice();
            const props = e.features[0].properties;

            new mapboxgl.Popup()
              .setLngLat(coords)
              .setHTML(`
                <div class="p-2">
                  <div class="font-semibold">${props.title}</div>
                  <div class="text-sm">${props.shopSign}</div>
                  <div class="text-sm mt-1">${props.isPresent ? 'Produit présent' : 'Produit absent'}</div>
                  ${props.reason !== 'Non spécifié' ? `<div class="text-sm"><span class="font-medium">Raison:</span> ${props.reason}</div>` : ''}
                </div>
              `)
              .addTo(map);
          });

          // Curseur avec IDs uniques
          const mouseEnterId = `mouseenter-${id}-${Date.now()}`;
          eventIds.push({ type: 'mouseenter', layer: `${id}-layer`, id: mouseEnterId });
          map.on('mouseenter', `${id}-layer`, () => {
            map.getCanvas().style.cursor = 'pointer';
          });

          const mouseLeaveId = `mouseleave-${id}-${Date.now()}`;
          eventIds.push({ type: 'mouseleave', layer: `${id}-layer`, id: mouseLeaveId });
          map.on('mouseleave', `${id}-layer`, () => {
            map.getCanvas().style.cursor = '';
          });

          // Mettre à jour les IDs d'événements
          mapContainer.current._eventIds = eventIds;

          return true;
        } catch (error) {
          console.error(`Erreur lors de l'ajout de la couche ${id}:`, error);
          return false;
        }
      };

      // Ajouter les couches pour chaque type de magasin
      let presentAdded = addStoreLayer('present-stores', presentStores, '#10B981');
      let absentAdded = addStoreLayer('absent-stores', absentStores, '#EF4444');
      let outOfRangeAdded = false;

      // Ajouter les magasins hors référencement même si non visibles
      outOfRangeAdded = addStoreLayer('not-in-range-stores', notInRangeStores, '#9CA3AF');

      // Vérifier si des couches ont été ajoutées
      console.log(`Couches ajoutées: present=${presentAdded}, absent=${absentAdded}, outOfRange=${outOfRangeAdded}`);
    }

    // Ajuster le cadre pour montrer tous les points
    const bounds = new mapboxgl.LngLatBounds();

    // N'inclure que les magasins visibles pour l'ajustement des limites
    const storesForBounds = showOutOfRange
      ? validStores
      : [...presentStores, ...absentStores];

    if (storesForBounds.length > 0) {
      storesForBounds.forEach(store => {
        bounds.extend([parseFloat(store.longitude), parseFloat(store.latitude)]);
      });

      map.fitBounds(bounds, {
        padding: 50,
        maxZoom: 15
      });
    }

    setSourcesAdded(true);
  };

  return (
    <div
      ref={mapContainer}
      className={`w-full h-full rounded-lg shadow-md ${isVisible ? 'block' : 'hidden'}`}
      style={{ minHeight: '500px' }}
    />
  );
};

const DNMap = ({
  fetchWithAuth,
  selectedProject,
  selectedShopSigns,
  selectedCategories,
  currentUser,
  onTabChange,
  obfuscate
}) => {
  const [activeVisualization, setActiveVisualization] = useState('issues');
  const [loading, setLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productStoresData, setProductStoresData] = useState(null);
  const [showOutOfRange, setShowOutOfRange] = useState(true);
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [selectedIssueProducts, setSelectedIssueProducts] = useState([]);

  const { issues, products, startDate, endDate } = useDN();
  const selectStyles = useSelectStyles();
  const location = useLocation();

  // Récupérer les paramètres depuis l'URL
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const vizFromUrl = queryParams.get('viz'); // 'issues' ou 'product'
    const productFromUrl = queryParams.get('product');

    // Si l'URL contient un type de visualisation valide
    if (vizFromUrl && ['issues', 'product'].includes(vizFromUrl)) {
      setActiveVisualization(vizFromUrl);
    }

    // Si l'URL contient un produit et que la visualisation est 'product'
    if (productFromUrl && products?.length > 0 && (vizFromUrl === 'product' || activeVisualization === 'product')) {
      const productObj = products.find(p => p.value === productFromUrl);
      if (productObj) {
        setSelectedProduct(productObj);
      }
    }
  }, [location, products]);

  // Créer les options pour le filtre de raisons en fonction des produits sélectionnés
  const reasonOptions = useMemo(() => {
    if (!issues || !issues.length) return [];

    // Si des produits sont sélectionnés, ne montrer que les raisons associées à ces produits
    let filteredIssuesForReasons = [...issues];

    if (selectedIssueProducts && selectedIssueProducts.length > 0) {
      const productValues = selectedIssueProducts.map(product => product.value);
      filteredIssuesForReasons = filteredIssuesForReasons.filter(issue =>
        issue.productName && productValues.includes(issue.productName)
      );
    }

    // Extraire toutes les raisons uniques des issues filtrées
    const uniqueReasons = [...new Set(filteredIssuesForReasons
      .map(issue => issue.reason)
      .filter(Boolean))]
      .sort()
      .map(reason => ({ value: reason, label: reason }));

    // Ajouter une option pour les raisons non spécifiées si nécessaire
    if (filteredIssuesForReasons.some(issue => !issue.reason)) {
      uniqueReasons.push({ value: 'Non spécifié', label: 'Non spécifié' });
    }

    return uniqueReasons;
  }, [issues, selectedIssueProducts]);

  // Créer les options pour le filtre de produits en fonction des raisons sélectionnées
  const issueProductOptions = useMemo(() => {
    if (!issues || !issues.length) return [];

    // Si des raisons sont sélectionnées, ne montrer que les produits associés à ces raisons
    let filteredIssuesForProducts = [...issues];

    if (selectedReasons && selectedReasons.length > 0) {
      const reasonValues = selectedReasons.map(reason => reason.value);
      filteredIssuesForProducts = filteredIssuesForProducts.filter(issue => {
        const issueReason = issue.reason || 'Non spécifié';
        return reasonValues.includes(issueReason);
      });
    }

    // Extraire tous les produits uniques des issues filtrées
    const uniqueProducts = [...new Set(filteredIssuesForProducts
      .map(issue => issue.productName)
      .filter(Boolean))]
      .sort()
      .map(productName => ({ value: productName, label: productName }));

    return uniqueProducts;
  }, [issues, selectedReasons]);

  // Filtrer les issues en fonction des enseignes, catégories, raisons et produits sélectionnés
  const filteredIssues = useMemo(() => {
    if (!issues || !issues.length) return [];

    let result = [...issues];

    if (selectedShopSigns && selectedShopSigns.length > 0) {
      const shopSignValues = selectedShopSigns.map(sign => sign.value);
      result = result.filter(issue => shopSignValues.includes(issue.shopSign));
    }

    if (selectedCategories && selectedCategories.length > 0) {
      const categoryValues = selectedCategories.map(cat => cat.value);
      result = result.filter(issue => categoryValues.includes(issue.category));
    }

    // Filtrer par raisons sélectionnées
    if (selectedReasons && selectedReasons.length > 0) {
      const reasonValues = selectedReasons.map(reason => reason.value);
      result = result.filter(issue => {
        // Gérer le cas des raisons non spécifiées
        const issueReason = issue.reason || 'Non spécifié';
        return reasonValues.includes(issueReason);
      });
    }

    // Filtrer par produits sélectionnés
    if (selectedIssueProducts && selectedIssueProducts.length > 0) {
      const productValues = selectedIssueProducts.map(product => product.value);
      result = result.filter(issue =>
        issue.productName && productValues.includes(issue.productName)
      );
    }

    return result.filter(issue =>
      issue.latitude && issue.longitude &&
      !isNaN(parseFloat(issue.latitude)) &&
      !isNaN(parseFloat(issue.longitude))
    );
  }, [issues, selectedShopSigns, selectedCategories, selectedReasons, selectedIssueProducts]);

  // Fetch product data
  useEffect(() => {
    if (!selectedProduct || activeVisualization !== 'product' || !selectedProject) return;

    const fetchProductData = async () => {
      try {
        setLoading(true);

        const formattedStartDate = startDate.toISOString().split('T')[0];
        const formattedEndDate = endDate.toISOString().split('T')[0];

        const url = `${API_BASE_URL}/api/dwh/dn/${selectedProject.value}/products/details/${encodeURIComponent(selectedProduct.value)}?startDate=${formattedStartDate}&endDate=${formattedEndDate}`;

        const response = await fetchWithAuth(url);

        if (response.ok) {
          const data = await response.json();
          setProductStoresData(data);
        } else {
          console.error('Failed to fetch product details');
        }
      } catch (error) {
        console.error('Error fetching product details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProductData();
  }, [selectedProduct, activeVisualization, fetchWithAuth, selectedProject, startDate, endDate]);

  // Render filters en ligne sur desktop
  const renderFilters = () => {
    if (activeVisualization === 'issues') {
      return (
        <div className="mb-4">
          <div className="flex flex-col md:flex-row md:space-x-3 space-y-3 md:space-y-0">
            <div className="md:w-1/2">
              <Select
                options={obfuscate ? issueProductOptions.map(product => ({
                  ...product,
                  label: `${product.label.slice(0, 2)}${'*'.repeat(Math.max(0, product.label.length - 2))}`
                })) : issueProductOptions}
                value={obfuscate ? selectedIssueProducts.map(product => ({
                  ...product,
                  label: `${product.label.slice(0, 2)}${'*'.repeat(Math.max(0, product.label.length - 2))}`
                })) : selectedIssueProducts}
                onChange={setSelectedIssueProducts}
                placeholder={_t("Filter by product...")}
                noOptionsMessage={() => _t("No product found")}
                styles={selectStyles}
                isSearchable={true}
                isClearable={true}
                isMulti={true}
              />
            </div>
            <div className="md:w-1/2">
              <Select
                options={reasonOptions}
                value={selectedReasons}
                onChange={setSelectedReasons}
                placeholder={_t("Filter by reason...")}
                noOptionsMessage={() => _t("No reason found")}
                styles={selectStyles}
                isSearchable={true}
                isClearable={true}
                isMulti={true}
              />
            </div>
          </div>
        </div>
      );
    }

    if (activeVisualization === 'product') {
      return (
        <div className="mb-4 space-y-3">
          <Select
            options={obfuscate ? products.map(product => ({
              ...product,
              label: `${product.label.slice(0, 2)}${'*'.repeat(Math.max(0, product.label.length - 2))}`
            })) : products}
            value={selectedProduct}
            onChange={setSelectedProduct}
            placeholder={_t("Select a product...")}
            noOptionsMessage={() => _t("No product found")}
            styles={selectStyles}
            isSearchable={true}
            isClearable={false}
          />
          <div className="flex justify-end">
            <ToggleSwitch
              label={_t("Display stores not in store range")}
              checked={showOutOfRange}
              onChange={() => setShowOutOfRange(!showOutOfRange)}
            />
          </div>
        </div>
      );
    }

    return null;
  };

  // Render stats avec affichage amélioré
  const renderStats = () => {
    if (activeVisualization === 'issues') {
      // Calculer les statistiques pour les raisons et produits sélectionnés
      const reasonStats = selectedReasons.length > 0
        ? selectedReasons.map(reason => {
            const count = filteredIssues.filter(issue =>
              (issue.reason || 'Non spécifié') === reason.value
            ).length;
            return { reason: reason.value, count };
          })
        : [];

      const productStats = selectedIssueProducts.length > 0
        ? selectedIssueProducts.map(product => {
            const count = filteredIssues.filter(issue =>
              issue.productName === product.value
            ).length;
            return { product: product.value, count };
          })
        : [];

      // Calculer des statistiques combinées pour afficher quand on a sélectionné à la fois des produits et des raisons
      const combinedStats = (selectedIssueProducts.length > 0 && selectedReasons.length > 0)
        ? selectedIssueProducts.flatMap(product =>
            selectedReasons.map(reason => {
              const count = filteredIssues.filter(issue =>
                issue.productName === product.value &&
                (issue.reason || 'Non spécifié') === reason.value
              ).length;
              return { product: product.value, reason: reason.value, count };
            })
          ).filter(stat => stat.count > 0)
        : [];

      return (
        <div className="bg-white dark:bg-gray-700 rounded-lg shadow p-3 mb-4">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {_t('Distribution problems')}
          </h3>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <div className="mb-2">
              <strong>{filteredIssues.length}</strong> {_t('problems displayed on the map')}
            </div>

            {selectedIssueProducts.length > 0 && selectedReasons.length === 0 && (
              <div className="mt-2 mb-2">
                <div className="font-medium border-b pb-1 mb-1">{_t('Selected products')}:</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                  {productStats.map(stat => (
                    <div key={stat.product} className="flex justify-between">
                      <span className="truncate mr-2" title={stat.product}>{obfuscate ? `${stat.product.slice(0, 2)}${'*'.repeat(Math.max(0, stat.product.length - 2))}` : stat.product}:</span>
                      <span>{stat.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedReasons.length > 0 && selectedIssueProducts.length === 0 && (
              <div className="mt-2">
                <div className="font-medium border-b pb-1 mb-1">{_t('Selected reasons')}:</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                  {reasonStats.map(stat => (
                    <div key={stat.reason} className="flex justify-between">
                      <span className="truncate mr-2" title={stat.reason}>{stat.reason}:</span>
                      <span>{stat.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedIssueProducts.length > 0 && selectedReasons.length > 0 && (
              <div className="mt-2">
                <div className="font-medium border-b pb-1 mb-1">{_t('Product/reason intersection')}:</div>
                <div className="grid grid-cols-1 gap-1">
                  {combinedStats.map((stat, index) => (
                    <div key={index} className="flex justify-between">
                      <span className="truncate mr-2" title={`${stat.product} - ${stat.reason}`}>
                        {stat.product} - {stat.reason}:
                      </span>
                      <span>{stat.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }

    if (activeVisualization === 'product' && productStoresData) {
      const { stats } = productStoresData;

      return (
        <div className="bg-white dark:bg-gray-700 rounded-lg shadow p-3 mb-4">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {selectedProduct?.label || _t('Selected product')}
            </h3>
            <a
              href={`#/dn-viewer?tab=store-details&product=${encodeURIComponent(selectedProduct?.value || '')}`}
              className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200 px-2 py-1 rounded flex items-center"
              onClick={() => onTabChange && onTabChange('store-details')}
            >
              <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              {_t('Store details')}
            </a>
          </div>
          <div className="flex flex-wrap gap-2 text-xs">
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full bg-emerald-500 mr-1"></div>
              <span>{stats?.presentStores || 0} {_n('present', 'presents', stats?.presentStores || 0)}</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full bg-red-500 mr-1"></div>
              <span>{stats?.notPresentStores || 0} {_n('absent', 'absents', stats?.notPresentStores || 0)}</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full bg-gray-400 mr-1"></div>
              <span>{stats?.notInRangeStores || 0} {_n('not in store range', 'not in store ranges', stats?.notInRangeStores || 0)}</span>
            </div>
            <div className="px-2 py-0.5 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full">
              {_t('ND rate')}: {Math.round(stats?.dnRate || 0)}%
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
        <MapVisualizationSelector
          activeViz={activeVisualization}
          onChange={(viz) => {
            setActiveVisualization(viz);
            // Réinitialiser les filtres si on change de vue
            if (viz === 'issues') {
              setSelectedProduct(null);
            } else {
              setSelectedReasons([]);
              setSelectedIssueProducts([]);
            }
          }}
        />
      </div>

      {renderFilters()}
      {renderStats()}

      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <LoadingSpinner />
        </div>
      ) : (
        <div className="flex-1" style={{ minHeight: '500px', position: 'relative' }}>
          <MapInstance
            type="issues"
            data={filteredIssues}
            isVisible={activeVisualization === 'issues'}
          />

          <MapInstance
            type="product"
            data={productStoresData}
            isVisible={activeVisualization === 'product'}
            showOutOfRange={showOutOfRange}
          />
        </div>
      )}
    </div>
  );
};

export default DNMap;
